/**
 * 应用配置工具函数
 * 用于区分客户端和运营端的配置
 */
import { SourceFrom } from '@/enums/auth'

export type AppType = 'client' | 'admin'

/**
 * 获取当前应用类型
 * @returns {AppType} 应用类型 - 'client' | 'admin'
 */
export function getAppType(): AppType {
  // 优先从环境变量获取
  const envAppType = import.meta.env.VITE_APP_TYPE
  if (envAppType === 'client' || envAppType === 'admin') {
    return envAppType
  }

  // 从全局变量获取（构建时注入）
  if (typeof (globalThis as any).__APP_TYPE__ !== 'undefined') {
    return (globalThis as any).__APP_TYPE__
  }

  // 默认返回客户端
  return 'client'
}

/**
 * 获取当前环境
 * @returns {string} 环境名称
 */
export function getAppEnv(): string {
  return import.meta.env.VITE_ENV || import.meta.env.MODE || 'development'
}

/**
 * 是否为客户端
 * @returns {boolean}
 */
export function isClient(): boolean {
  return getAppType() === 'client'
}

/**
 * 是否为运营端
 * @returns {boolean}
 */
export function isAdmin(): boolean {
  return getAppType() === 'admin'
}

/**
 * 是否为开发环境
 * @returns {boolean}
 */
export function isDevelopment(): boolean {
  return getAppEnv() === 'development'
}

/**
 * 开发环境是否展示全量菜单（跳过权限过滤，仅 local dev 使用）
 */
export function isDevFullMenu(): boolean {
  return isDevelopment() && import.meta.env.VITE_DEV_FULL_MENU === 'true'
}

/**
 * 是否为测试环境
 * @returns {boolean}
 */
export function isTest(): boolean {
  return getAppEnv() === 'test'
}

/**
 * 是否为生产环境
 * @returns {boolean}
 */
export function isProduction(): boolean {
  return getAppEnv() === 'production'
}

/**
 * 检测URL参数中的来源标识
 * @returns {string} 来源标识
 */
export function getSourceFromUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('from')
}

/**
 * 检测URL参数中的Code
 * @returns {string} Code参数
 */
export function getCodeFromUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('code')
}

/**
 * 获取登录页面路由
 * @param curSource 当前存储的路由
 */
export function getLoginRoute(curSource?: string): string {
  curSource = curSource || getSourceFromUrl()
  if (isAdmin()) {
    if ( curSource === 'home') {
      return '/auth/tenant'
    }
    return '/auth/staff'
  }
  return '/auth/portal'
}

/**
 * 获取应用标题
 * @returns {string}
 */
export function getAppTitle(): string {
  const baseTitle = import.meta.env.VITE_APP_TITLE || 'Control Tower 管理系统'
  const appType = getAppType()

  if (appType === 'admin') {
    return `${baseTitle} - 运营端`
  }

  return `${baseTitle} - 客户端`
}

/**
 * 判断是否应该显示返回按钮
 * 基于 Pinia store 中的持久化来源参数
 */
export function shouldShowReturnButton(): boolean {
  // 从URL参数检查来源，根据应用类型获取对应的存储key
  const appType = getAppType()
  const authStore = JSON.parse(localStorage.getItem(`ct_auth_store_${appType}`) || '{}')
  const sourceFromStorage = authStore.sourceFrom
  return  sourceFromStorage === SourceFrom.PORTAL || sourceFromStorage === SourceFrom.PORTAL_NEW
}

/**
 * 获取报告服务器地址
 * @returns {string} 报告服务器地址
 */
export function getReportServerHost(): string {
  // 优先从环境变量获取
  const envHost = import.meta.env.VITE_REPORT_SERVER_BASE_URL
  if (envHost) {
    return envHost
  }
  // 默认返回本地开发地址
  return 'http://localhost:8090'
}

/**
 * 应用配置对象
 */
export const appConfig = {
  type: getAppType(),
  env: getAppEnv(),
  title: getAppTitle(),
  version: import.meta.env.VITE_APP_VERSION || '1.0.0',
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
  apiTimeout: Number(import.meta.env.VITE_API_TIMEOUT) || 10000,
  devTools: import.meta.env.VITE_DEV_TOOLS === 'true',
  logLevel: import.meta.env.VITE_LOG_LEVEL || 'warn',
  loginRoute: getLoginRoute(),
  reportServerHost: getReportServerHost()
}
